import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Save, AlertCircle, Check } from 'lucide-react';
import { apiService, DealershipAccessMatrix } from '../../services/api';
import { DEALERSHIP_ROLES } from '../../config/navigationCatalog';
import { useAccessControl } from '../../contexts/AccessControlContext';

export const RoleAccessPage: React.FC = () => {
  const { t } = useTranslation();
  const { canAccess, refreshPermissions } = useAccessControl();
  const [matrix, setMatrix] = useState<DealershipAccessMatrix | null>(null);
  const [activeRole, setActiveRole] = useState<string>('manager');
  const [selected, setSelected] = useState<Record<string, Set<string>>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getDealershipAccessMatrix();
      setMatrix(data);
      const map: Record<string, Set<string>> = {};
      for (const block of data.roles) {
        map[block.role] = new Set(
          block.permissions.filter(p => p.granted).map(p => p.key),
        );
      }
      setSelected(map);
    } catch {
      setError(t('roleAccess.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const navPermissions = useMemo(() => {
    const block = matrix?.roles.find(r => r.role === activeRole);
    if (!block) return [];
    return block.permissions
      .filter(p => p.type === 'navigation')
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [matrix, activeRole]);

  const toggle = (key: string) => {
    if (activeRole === 'owner') return;
    setSelected(prev => {
      const set = new Set(prev[activeRole] ?? []);
      if (set.has(key)) {
        set.delete(key);
      } else {
        set.add(key);
      }
      return { ...prev, [activeRole]: set };
    });
  };

  const handleSave = async () => {
    if (!canAccess('users.roles')) return;
    try {
      setSaving(true);
      setError(null);
      const roles = DEALERSHIP_ROLES.filter(r => r !== 'owner').map(role => ({
        role,
        permissionKeys: Array.from(selected[role] ?? []),
      }));
      const updated = await apiService.updateDealershipAccess({ roles });
      setMatrix(updated);
      const map: Record<string, Set<string>> = {};
      for (const block of updated.roles) {
        map[block.role] = new Set(
          block.permissions.filter(p => p.granted).map(p => p.key),
        );
      }
      setSelected(map);
      await refreshPermissions();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      setError(t('roleAccess.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (!canAccess('users.roles')) {
    return (
      <div className="p-6">
        <p className="text-accent">{t('roleAccess.ownerOnly')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin h-10 w-10 border-2 border-accent rounded-full border-t-transparent" />
      </div>
    );
  }

  const groupedNav = navPermissions.reduce<Record<string, typeof navPermissions>>((acc, p) => {
    const section = p.parentKey ?? p.key;
    if (!acc[section]) acc[section] = [];
    acc[section].push(p);
    return acc;
  }, {});

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Shield className="h-8 w-8 text-accent-600" />
        <div>
          <h1 className="text-2xl font-bold text-primary-900 dark:text-white">{t('roleAccess.title')}</h1>
          <p className="text-sm text-accent">{t('roleAccess.subtitle')}</p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-sm">
          <Check className="h-4 w-4 shrink-0" />
          {t('roleAccess.saveSuccess')}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {DEALERSHIP_ROLES.map(role => (
          <button
            key={role}
            type="button"
            onClick={() => setActiveRole(role)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeRole === role
                ? 'bg-accent-600 text-white'
                : 'bg-card border border-border text-accent hover:bg-accent/5'
            }`}
          >
            {t(`roleAccess.roles.${role}`)}
          </button>
        ))}
      </div>

      {activeRole === 'owner' ? (
        <p className="text-sm text-accent">{t('roleAccess.ownerFullAccess')}</p>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedNav).map(([section, items]) => (
            <div key={section} className="rounded-xl border border-border bg-card p-4">
              <h2 className="text-sm font-semibold text-primary-900 dark:text-white mb-3">
                {items[0]?.parentKey
                  ? t(items[0].labelKey ?? items[0].key, { defaultValue: items[0].key })
                  : t('roleAccess.sectionRoot')}
              </h2>
              <div className="grid gap-2 sm:grid-cols-2">
                {items.map(item => {
                  const checked = selected[activeRole]?.has(item.key) ?? false;
                  return (
                    <label
                      key={item.key}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-accent/5 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(item.key)}
                        className="h-4 w-4 rounded border-border text-accent-600 focus:ring-accent-500"
                      />
                      <span className="text-sm text-primary-800 dark:text-primary-100">
                        {t(item.labelKey ?? item.key, { defaultValue: item.key })}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeRole !== 'owner' && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-accent-600 text-white font-medium hover:bg-accent-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {saving ? t('roleAccess.saving') : t('roleAccess.save')}
          </button>
        </div>
      )}
    </div>
  );
};
